// Tartan search: matches the store's tartan list (assets/tartans.json) and the collaboration aliases against the
// search terms and renders the matches into every [data-tartan-search] placeholder, in the search drawer and on /search.
(function () {
  const IGNORED = ['tartan', 'tartans', 'watch', 'watches', 'strap', 'straps', 'roll', 'rolls', 'apple', 'samsung', 'clan', 'custom', 'the', 'mac'];
  const MIN_TOKEN = 3;
  // Version words narrow a clan search ("fraser modern") but never start one ("modern" alone)
  const VERSIONS = ['modern', 'ancient', 'weathered', 'hunting', 'dress', 'muted', 'red', 'grey', 'gray', 'green', 'blue', 'black', 'reproduction', 'antique'];
  let tartansPromise = null;
  let surnamesPromise = null;
  // Irish county, province and national tartans, shown for O' names
  const IRISH = /\b(irish|ireland|county|ulster|munster|leinster|connacht|connaught|erin|eire)\b/i;

  function words(text) {
    return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim().split(' ')
      .filter(Boolean).map((w) => (w.startsWith('mc') ? 'mac' + w.slice(2) : w));
  }

  // "MacDonald" is also findable as "donald"
  function indexWords(text) {
    const list = words(text);
    list.slice().forEach((w) => { if (w.startsWith('mac') && w.length > 5) list.push(w.slice(3)); });
    return list;
  }

  function slug(text) {
    return words(text).join('-');
  }

  function loadTartans(url) {
    if (!tartansPromise) {
      tartansPromise = fetch(url).then((r) => r.json())
        .then((list) => list.filter((t) => t.name !== 'Add a Custom Tartan').map((t) => ({ name: t.name, image: t.image, words: indexWords(t.name), joined: words(t.name).join('') })))
        .catch(() => []);
    }
    return tartansPromise;
  }

  // Surname -> clans, from the client's own mapping (assets/tartan-surnames.json)
  function loadSurnames(url) {
    if (!surnamesPromise) surnamesPromise = url ? fetch(url).then((r) => r.json()).catch(() => ({})) : Promise.resolve({});
    return surnamesPromise;
  }

  // Tartans a surname leads to: its clans' tartans in the order given, or Irish tartans for O' names
  function connectedTartans(tartans, surnames, terms) {
    const key = words(terms).filter((w) => !IGNORED.includes(w)).join('');
    const entry = surnames[key];
    if (entry) {
      const seen = new Set();
      const list = [];
      entry.clans.forEach((clan) => {
        const clanWords = words(clan);
        tartans.filter((t) => clanWords.every((w, i) => t.words[i] === w)).forEach((t) => { if (!seen.has(t.name)) { seen.add(t.name); list.push(t); } });
      });
      return { list, why: entry.why };
    }
    if (/^\s*o\s*['’]\s*[a-z]/i.test(terms) || /^\s*o\s[a-z]{3,}/i.test(terms)) {
      return { list: tartans.filter((t) => IRISH.test(t.name)), why: 'Most names beginning with O’ are Irish, so here are our Irish tartans' };
    }
    return { list: [], why: '' };
  }

  function tokensOf(terms) {
    const tokens = words(terms).filter((w) => w.length >= MIN_TOKEN && !IGNORED.includes(w));
    return tokens.every((w) => VERSIONS.includes(w)) ? [] : tokens;
  }

  // One typo allowed in words of 6+ letters ("macaulay" finds MacAuley, "mcallister" finds MacAlister)
  function closeEnough(tok, word) {
    if (tok.length < 6) return false;
    const a = tok, b = word.slice(0, tok.length + 1);
    const row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      let prev = row[0];
      row[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const tmp = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev = tmp;
      }
    }
    return Math.min(...row.slice(Math.max(a.length - 1, 0))) <= 1;
  }

  function tokenMatches(tok, t) {
    return t.words.some((w) => w.startsWith(tok) || closeEnough(tok, w)) || t.joined.startsWith(tok);
  }

  function matchTartans(tartans, tokens) {
    if (!tokens.length) return [];
    return tartans
      .filter((t) => tokens.every((tok) => tokenMatches(tok, t)))
      .sort((a, b) => (b.words[0].startsWith(tokens[0]) ? 1 : 0) - (a.words[0].startsWith(tokens[0]) ? 1 : 0) || a.name.localeCompare(b.name));
  }

  function matchAliases(aliases, tokens) {
    if (!tokens.length) return [];
    return aliases.filter((a) => tokens.some((tok) => a.keys.some((k) => k.startsWith(tok))));
  }

  function thumb(image) {
    return image + (image.includes('?') ? '&' : '?') + 'width=120';
  }

  function tile(name, image, href, note) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'tartan-search__item';
    a.href = href;
    const img = document.createElement('img');
    img.src = thumb(image);
    img.alt = '';
    img.width = 48;
    img.height = 48;
    img.loading = 'lazy';
    const label = document.createElement('span');
    label.className = 'tartan-search__name';
    label.textContent = name.replace(/&amp;/g, '&');
    a.append(img, label);
    if (note) {
      const small = document.createElement('span');
      small.className = 'tartan-search__note';
      small.textContent = note;
      label.appendChild(small);
    }
    li.appendChild(a);
    return li;
  }

  function render(el, tartans, surnames) {
    const terms = el.dataset.terms || '';
    const tokens = tokensOf(terms);
    const limit = parseInt(el.dataset.limit || '6', 10);
    let aliases = [];
    try { aliases = JSON.parse(el.dataset.aliases || '[]'); } catch (e) { aliases = []; }
    const collabs = matchAliases(aliases, tokens);
    const connected = connectedTartans(tartans, surnames, terms);
    const direct = matchTartans(tartans, tokens).filter((t) => !connected.list.includes(t));
    const found = connected.list.concat(direct);
    el.dataset.done = 'true';
    const total = collabs.length + found.length;
    if (!total) return;

    const heading = document.createElement('p');
    heading.className = 'tartan-search__heading';
    heading.id = el.id + '-heading';
    heading.textContent = found.length
      ? (found.length === 1 ? '1 tartan' : found.length + ' tartans') + ' · pick one for your watch'
      : (collabs.every((c) => c.note) ? 'Coming soon' : 'Collaborations');

    const list = document.createElement('ul');
    list.className = 'tartan-search__list';
    list.setAttribute('aria-labelledby', heading.id);
    collabs.forEach((c) => list.appendChild(tile(c.title, c.image, c.url, c.note || 'Collaboration collection')));
    found.slice(0, Math.max(limit - collabs.length, 0)).forEach((t) => {
      list.appendChild(tile(t.name, t.image, el.dataset.productUrl + '?tartan=' + slug(t.name)));
    });

    const head = document.createElement('div');
    head.className = 'tartan-search__head';
    head.appendChild(heading);
    if (total > limit && el.dataset.moreUrl) {
      const more = document.createElement('a');
      more.className = 'tartan-search__more link';
      more.href = el.dataset.moreUrl;
      more.textContent = 'See all';
      more.setAttribute('aria-label', 'See all ' + found.length + ' tartans');
      head.appendChild(more);
    }
    el.append(head);
    if (connected.why) {
      const why = document.createElement('p');
      why.className = 'tartan-search__why';
      why.textContent = connected.why + '.';
      el.appendChild(why);
    }
    el.append(list);
    el.hidden = false;

    // In the drawer, a tartan match means the search did find something
    const noResults = el.parentElement && el.parentElement.querySelector('.predictive-search__no-results');
    if (noResults) noResults.hidden = true;
    // On an empty /search page, say it is products that were not found
    const noProducts = document.querySelector('[data-no-results-text]');
    if (noProducts && el.id === 'tartan-search-page-empty') noProducts.textContent = noProducts.dataset.noProductsText;

    // On /search a tartan match becomes the first tab and opens by default; Products follows
    const panel = el.closest('[data-tartan-panel]');
    const tab = panel && document.querySelector('[data-tartan-tab]');
    if (tab) {
      tab.textContent = (found.length ? 'Tartans' : collabs.every((c) => c.note) ? 'Coming soon' : 'Collaborations') + ' (' + total + ')';
      tab.hidden = false;
      const firstTab = tab.parentElement.querySelector('[role="tab"][slot="title"]');
      if (firstTab && firstTab !== tab) {
        tab.parentElement.insertBefore(panel, firstTab);
        tab.parentElement.insertBefore(tab, panel);
      }
      requestAnimationFrame(() => tab.click());
    }
  }

  function scan() {
    document.querySelectorAll('[data-tartan-search]:not([data-done])').forEach((el) => {
      el.dataset.done = 'pending';
      Promise.all([loadTartans(el.dataset.source), loadSurnames(el.dataset.surnames)]).then(([tartans, surnames]) => render(el, tartans, surnames));
    });
  }

  scan();
  // The search drawer re-renders its results on every keystroke
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
})();
